/**
 * The maths behind Compare's you-vs-them chart.
 *
 * ONE WINDOW, REAL TOTALS, NOTHING LEFT OUT. Every point either trainer has is
 * drawn at its real value on a single axis that holds all of them. Height is a
 * dex total and nothing else: the higher line is the bigger dex, the distance
 * between the lines is the gap, and a reader can put a number on any height.
 *
 * This replaced a cleverer scheme and is a deliberate trade. That one had two
 * modes: an absolute window zoomed to a tight band around where the pair stood
 * TODAY, and — once they were more than CLOSE_RANGE apart — a relative mode
 * that redrew each line from its own starting value so a single catch stayed a
 * visible step. It kept +1 legible. What it cost was the truth of the picture:
 * on a 825-vs-722 Shiny dex the friend's line was a flat thread near the floor
 * and their 722 appeared nowhere on the plot, while the zoomed window quietly
 * dropped the earlier, lower part of both journeys off the bottom of the frame.
 * Reported on device 2026-08-13: "it doesn't show Caprifunn getting up to 722".
 *
 * A chart that hides two thirds of its own data to make one Pokémon visible is
 * answering a question nobody asked. So the geometry is now plain, and the
 * PRECISION is carried by numbers instead: every point where a line moves is
 * labelled on the point with its +N/−N, the legend states each line's total and
 * what it gained, and the gap badge names who is ahead and by how much. A catch
 * too small to see is still a catch you can read.
 */

export interface HistorySeriesPoint {
  day: string;
  count: number;
  live: boolean;
  carried: boolean;
}

export interface TradeMarker {
  id: number;
  day: string;
  mineSpeciesId: number;
  theirsSpeciesId: number;
}

export interface ChartPoint {
  day: string;
  /** 0..1 across the plot, left to right. */
  x: number;
  /** 0..1 up the plot. 1 is the top. */
  y: number;
  count: number;
  /** Change from the previous point on this line. 0 on the first point. */
  delta: number;
  live: boolean;
  carried: boolean;
}

/**
 * What one line DID over the window, for the legend beside its total.
 *
 * The climb is drawable now — the window holds the whole period — but a slope
 * is not a number, and "how much did I add this week" deserves one.
 */
export interface LineSummary {
  /** Change across the whole window. Negative if a dex shrank. */
  climb: number;
  /** First and last known counts in the window. */
  startCount: number | null;
  endCount: number | null;
  /** True when there is one observation — a point, with no line to draw. */
  singlePoint: boolean;
}

export interface ChartModel {
  days: string[];
  mine: ChartPoint[];
  theirs: ChartPoint[];
  /** Per-line facts for the on-chart labels. */
  mineSummary: LineSummary;
  theirsSummary: LineSummary;
  /** The y-span in Pokémon. Both lines share it, because there is only one. */
  span: number;
  /** The floor and ceiling as real dex totals. */
  axis: { bottom: number; top: number };
  /** Latest counts, for the "You 704 · Ash 312" line. */
  lastMine: number | null;
  lastTheirs: number | null;
  /** Positive = I am ahead. null when either side has no data. */
  gap: number | null;
  markers: Array<TradeMarker & { x: number }>;
}

/**
 * Smallest y-span the window may collapse to.
 *
 * A flat fortnight would otherwise have zero range, and every point would map
 * to the same y (or divide by zero). Four means a single catch moves the line a
 * visible quarter of the plot without making a flat line look like a heartbeat.
 */
export const MIN_Y_SPAN = 4;

/** Pad the window so a line at the extreme is not drawn on the frame itself. */
const PAD_FRACTION = 0.15;

function toMap(points: HistorySeriesPoint[]): Map<string, HistorySeriesPoint> {
  return new Map(points.map((p) => [p.day, p]));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A day key as a person reads it: `2026-05-05` → `05May`.
 *
 * The surfaces around this chart were showing the bare tail of the key —
 * "08-05", "07-15" — which is ambiguous to most of the world and unreadable
 * next to a count. A named month cannot be misread as a day, and no separator
 * means it still fits under a tick.
 */
export function formatDayKey(day: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${m[3]}${month}` : day;
}

/**
 * A few evenly-spaced days to hang the x axis on — always including the first
 * and the last, because the ends are what give a reader the period.
 *
 * By INDEX, matching how the plot spaces its points: the axis is the list of
 * days either trainer has a reading for, not a calendar, so a tick has to land
 * on a day the chart actually drew.
 */
export function axisTicks(days: readonly string[], count = 3): Array<{ day: string; x: number }> {
  if (days.length === 0) return [];
  if (days.length === 1) return [{ day: days[0], x: 0.5 }];
  const wanted = Math.max(2, Math.min(count, days.length));
  const out: Array<{ day: string; x: number }> = [];
  for (let i = 0; i < wanted; i++) {
    const index = Math.round((i / (wanted - 1)) * (days.length - 1));
    const day = days[index];
    // Rounding can land twice on the same day on a very short axis.
    if (out.some((t) => t.day === day)) continue;
    out.push({ day, x: index / (days.length - 1) });
  }
  return out;
}

/** Move a YYYY-MM-DD day key by whole days. UTC, so it never drifts an hour. */
export function shiftDay(day: string, byDays: number): string {
  const at = Date.parse(`${day}T00:00:00.000Z`);
  if (!Number.isFinite(at)) return day;
  return new Date(at + byDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * Build the drawable model from two settled series.
 *
 * Both lines are placed on ONE shared day axis (the union of the two, sorted),
 * because a shared x is the only way "we were level on Tuesday" is readable. A
 * day where one trainer has no observation carries their previous value
 * forward — a missing point means "nobody looked", not "it went to zero", and
 * a line that dives to zero on a quiet day would be a lie.
 */
export function buildChartModel(
  mineSeries: HistorySeriesPoint[],
  theirsSeries: HistorySeriesPoint[],
  markers: TradeMarker[] = [],
): ChartModel {
  const days = Array.from(
    new Set([...mineSeries.map((p) => p.day), ...theirsSeries.map((p) => p.day)]),
  ).sort();

  const emptySummary: LineSummary = {
    climb: 0, startCount: null, endCount: null, singlePoint: false,
  };
  const empty: ChartModel = {
    days: [], mine: [], theirs: [],
    mineSummary: emptySummary, theirsSummary: emptySummary,
    span: MIN_Y_SPAN,
    axis: { bottom: 0, top: 0 },
    lastMine: null, lastTheirs: null, gap: null, markers: [],
  };
  if (days.length === 0) return empty;

  const mineByDay = toMap(mineSeries);
  const theirsByDay = toMap(theirsSeries);

  /** Walk the shared axis, carrying the last known value across quiet days. */
  const fill = (byDay: Map<string, HistorySeriesPoint>) => {
    const out: Array<{ day: string; count: number; live: boolean; carried: boolean }> = [];
    let last: number | null = null;
    for (const day of days) {
      const p = byDay.get(day);
      if (p) last = p.count;
      // Before a trainer's first observation there is nothing to carry, so the
      // line simply does not start yet.
      if (last == null) continue;
      out.push({ day, count: last, live: p?.live ?? false, carried: p?.carried ?? false });
    }
    return out;
  };

  const mineFilled = fill(mineByDay);
  const theirsFilled = fill(theirsByDay);

  const values = [...mineFilled, ...theirsFilled].map((p) => p.count);
  if (values.length === 0) return empty;

  /**
   * THE WINDOW HOLDS EVERYTHING BOTH TRAINERS DID, and nothing decides that but
   * the data.
   *
   * The floor is the lowest value on the plot and the ceiling the highest, so
   * no observation is off the frame, no line is clamped to an edge, and the two
   * lines are separated by exactly the gap between the trainers. A pair 100
   * apart look 100 apart; a pair who are level draw over each other, which is
   * what level looks like.
   *
   * What this deliberately gives up is the zoom. On a wide window a single
   * catch is a sub-pixel step — see the file header. The +N labels on the
   * points are the answer to that, not a tighter axis.
   */
  const low = Math.min(...values);
  const high = Math.max(...values);
  // Pad, then widen to the floor so a flat stretch draws down the middle rather
  // than pinned to an edge — and never divides by zero.
  const span = Math.max((high - low) * (1 + PAD_FRACTION * 2), MIN_Y_SPAN);
  const mid = (low + high) / 2;
  const bottom = mid - span / 2;

  const xFor = (day: string) =>
    days.length === 1 ? 0.5 : days.indexOf(day) / (days.length - 1);

  const project = (filled: ReturnType<typeof fill>): ChartPoint[] =>
    filled.map((p, i) => ({
      day: p.day,
      x: xFor(p.day),
      // No clamp is needed — the window was built to hold every value — but the
      // bound stays as a guard against a future scale that isn't.
      y: Math.min(1, Math.max(0, (p.count - bottom) / span)),
      count: p.count,
      delta: i === 0 ? 0 : p.count - filled[i - 1].count,
      live: p.live,
      carried: p.carried,
    }));

  const mine = project(mineFilled);
  const theirs = project(theirsFilled);
  const lastMine = mineFilled.length ? mineFilled[mineFilled.length - 1].count : null;
  const lastTheirs = theirsFilled.length ? theirsFilled[theirsFilled.length - 1].count : null;

  /** What a line did over the window, in numbers, for the legend. */
  const summarise = (filled: ReturnType<typeof fill>): LineSummary => {
    if (filled.length === 0) return emptySummary;
    const startCount = filled[0].count;
    const endCount = filled[filled.length - 1].count;
    return {
      climb: endCount - startCount,
      startCount,
      endCount,
      singlePoint: filled.length === 1,
    };
  };

  return {
    days,
    mine,
    theirs,
    mineSummary: summarise(mineFilled),
    theirsSummary: summarise(theirsFilled),
    span,
    // Real totals, because that is what a height is now worth.
    axis: { bottom: Math.round(bottom), top: Math.round(bottom + span) },
    lastMine,
    lastTheirs,
    gap: lastMine != null && lastTheirs != null ? lastMine - lastTheirs : null,
    // A marker for a day outside the window has nowhere to sit; dropping it is
    // better than clamping it to the edge, where it would claim a trade
    // happened on a day it did not.
    markers: markers
      .filter((m) => days.includes(m.day))
      .map((m) => ({ ...m, x: xFor(m.day) })),
  };
}

/**
 * The colour of the friend's line.
 *
 * IT IS THEIRS, NOT THE DEX'S. The friend's line used to take the colour of the
 * dex being charted, which on the Main dex is the same accent green the user's
 * own line is drawn in: two green lines, no legend on the plot, and no way to
 * tell whose was whose. Reported on device 2026-08-12.
 *
 * So it is the friend's own hue — the one their monogram already uses across
 * the app, derived from their account id — which makes the line the same colour
 * as the avatar the reader has been looking at all page. Hues that land near
 * the accent are rotated away, because the whole point is being able to tell
 * the two lines apart. Lightness comes from a token so the same hue reads on a
 * light card and a dark one.
 */
export const ACCENT_HUE = 142;
const HUE_CLEARANCE = 35;

export function friendLineHue(accountId: number): number {
  const hue = ((accountId * 47) % 360 + 360) % 360;
  const distance = Math.min(Math.abs(hue - ACCENT_HUE), 360 - Math.abs(hue - ACCENT_HUE));
  return distance < HUE_CLEARANCE ? (hue + 120) % 360 : hue;
}

export function friendLineColor(accountId: number): string {
  return `hsl(${friendLineHue(accountId)} 78% var(--chart-line-l, 52%))`;
}

/** SVG polyline points for a projected line, in a w×h box (y flipped). */
export function polylinePoints(points: ChartPoint[], w: number, h: number): string {
  return points.map((p) => `${(p.x * w).toFixed(2)},${((1 - p.y) * h).toFixed(2)}`).join(" ");
}

/** "+3" / "−1". Only called where delta is non-zero. */
export function formatDelta(delta: number): string {
  return delta > 0 ? `+${delta}` : `−${Math.abs(delta)}`;
}
